/**
 * NutriAssistant
 *
 * Der globale KI-Assistent: auf jeder App-Seite unten rechts, per
 * Command-Palette und per Strg/Cmd+J erreichbar. Ein Klick öffnet ein
 * kompaktes Chat-Panel.
 *
 * Voice flow:
 *   1. Hold / tap mic button → useVoiceRecorder starts recording
 *   2. Tap mic button again → stops, transcribes via Gladia EU (STT)
 *   3. Transcript is auto-sent to icarus-chat (Mistral, EU/France)
 *   4. Bot reply lands in the panel
 *
 * Text flow: type into composer → Enter / send button → same icarus-chat call.
 *
 * Kontext: Pfad und (falls vorhanden) Patienten-ID gehen an die Edge
 * Function, die daraus den „AKTIVER KONTEXT"-Block des Systemprompts baut
 * — nachdem sie die ID gegen die Organisation geprüft hat. Das Panel zeigt
 * denselben Kontext als Chip an; vorher stand der Chip da, ohne dass die
 * ID je beim Modell ankam.
 *
 * Der Name kommt aus `resolveAssistantName` (kanonisch „Dädalus",
 * überschreibbar per `profiles.assistant_name`) — nicht mehr als
 * hartkodiertes „Nutri", das dem Rest der App widersprach.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bot, Mic, MicOff, X, Minimize2, Square, Loader2, UserRound, PenSquare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { patientsRepo } from '@/data';
import { cn } from '@/lib/utils';
import { ChatBubble } from '@/components/chat/ChatBubble';
import { ChatComposer } from '@/components/chat/ChatComposer';
import { VoiceRecorderBar } from '@/components/chat/VoiceRecorderBar';
import { useVoiceRecorder } from '@/components/chat/useVoiceRecorder';
import { useIcarusChat } from './useIcarusChat';
import { OPEN_NUTRI_ASSISTANT_EVENT } from './openAssistant';

// Extract patient ID from paths like /patients/:id or /patients/:id/...
function extractPatientId(path: string): string | undefined {
  const m = path.match(/\/patients\/([a-f0-9-]{36})/i);
  return m?.[1];
}

export function NutriAssistant() {
  const location = useLocation();
  const patientId = extractPatientId(location.pathname);
  const { profile } = useAuth();
  const orgId = profile?.organization_id;

  // Kontext sichtbar machen (UX-Prinzip 1): Auf Patientenseiten zeigt das
  // Panel, ÜBER WEN der Chat gerade spricht. Name kommt über den Repo-Layer
  // (org-gescoped); ohne Patientenseite bleibt der Chip einfach weg.
  const { data: contextPatient } = useQuery({
    queryKey: ['nutri-assistant-context-patient', orgId, patientId],
    enabled: !!orgId && !!patientId,
    staleTime: 60_000,
    queryFn: () => patientsRepo.getPatient(orgId!, patientId!),
  });

  const chat = useIcarusChat({
    currentPath: location.pathname,
    patientId,
  });
  const assistantName = chat.assistantName;
  const hasConversation = chat.messages.length > 1;

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Chat-First: der Assistent ist von überall erreichbar — per
  // Command-Palette-Aktion (CustomEvent) und global per Strg/Cmd+J.
  // (Cmd+K gehört der Palette, Cmd+? dem Shortcut-Overlay.)
  useEffect(() => {
    const openFromEvent = () => setOpen(true);
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener(OPEN_NUTRI_ASSISTANT_EVENT, openFromEvent);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener(OPEN_NUTRI_ASSISTANT_EVENT, openFromEvent);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const voice = useVoiceRecorder({
    onTranscript: (text) => {
      chat.send(text);
    },
  });

  // Auto-scroll to the bottom on new messages
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [chat.messages, open]);

  const handleSend = (text: string) => {
    setInput('');
    chat.send(text);
  };

  const handleMicClick = () => {
    if (voice.state === 'idle') {
      voice.start();
    } else if (voice.state === 'recording') {
      voice.stop();
    }
  };

  const isVoiceActive = voice.state !== 'idle';

  return (
    <>
      {/* ── Floating panel ──────────────────────────────────────────── */}
      {open && (
        <div
          className={cn(
            'fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-50',
            'w-[340px] sm:w-[380px] max-h-[560px]',
            'flex flex-col rounded-2xl shadow-2xl border border-border/60',
            'bg-card text-foreground',
            'animate-in slide-in-from-bottom-4 fade-in duration-200',
          )}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border/40 shrink-0">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-none">{assistantName}</p>
              {contextPatient ? (
                <p
                  className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-primary"
                  title={`Kontext: ${contextPatient.first_name} ${contextPatient.last_name}`}
                >
                  <UserRound className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate">
                    Kontext: {contextPatient.first_name} {contextPatient.last_name}
                  </span>
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground mt-0.5">KI-Assistent · EU-Hosting</p>
              )}
            </div>
            {hasConversation && (
              <button
                onClick={chat.newConversation}
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Neues Gespräch"
                title="Neues Gespräch"
              >
                <PenSquare className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Schließen"
            >
              <Minimize2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0"
          >
            {chat.messages.map((msg) => (
              <div key={msg.id}>
                <ChatBubble
                  role={msg.role}
                  content={msg.pending && !msg.content ? '…' : msg.content}
                  avatar={
                    msg.role === 'assistant' ? (
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="w-3.5 h-3.5 text-primary" />
                      </div>
                    ) : undefined
                  }
                />
                {msg.error && (
                  <p className="text-xs text-destructive mt-1 pl-8">{msg.error}</p>
                )}
              </div>
            ))}

            {chat.isStreaming && chat.messages[chat.messages.length - 1]?.role !== 'assistant' && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                {assistantName} denkt…
              </div>
            )}
          </div>

          {/* Voice recording state */}
          {isVoiceActive && (
            <div className="px-3 pb-2 shrink-0">
              <VoiceRecorderBar
                state={voice.state === 'recording' ? 'recording' : 'transcribing'}
                onStop={voice.stop}
              />
            </div>
          )}

          {/* Composer */}
          {!isVoiceActive && (
            <div className="px-3 pb-3 pt-1 border-t border-border/30 shrink-0">
              <ChatComposer
                value={input}
                onChange={setInput}
                onSend={handleSend}
                disabled={chat.isStreaming}
                placeholder="Nachricht schreiben oder Mikrofon nutzen…"
                rows={1}
                trailing={
                  <button
                    type="button"
                    onClick={handleMicClick}
                    aria-label="Sprachnachricht"
                    className="h-[38px] w-[38px] shrink-0 flex items-center justify-center rounded-xl bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Mic className="w-4 h-4" />
                  </button>
                }
              />
            </div>
          )}

          {/* Stop streaming */}
          {chat.isStreaming && (
            <div className="px-3 pb-2 shrink-0 flex justify-end">
              <button
                onClick={chat.stop}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-lg hover:bg-secondary"
              >
                <Square className="w-3 h-3" />
                Stopp
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Floating trigger button ──────────────────────────────────── */}
      <button
        onClick={() => {
          if (isVoiceActive && voice.state === 'recording') {
            voice.stop();
            return;
          }
          setOpen((v) => !v);
        }}
        aria-label={open ? 'Assistent schließen' : `${assistantName} öffnen`}
        className={cn(
          'fixed bottom-[72px] right-4 lg:bottom-6 lg:right-6 z-50',
          // On desktop, shift left when panel is open to not overlap
          open && 'lg:right-[400px]',
          'w-12 h-12 rounded-full shadow-lg flex items-center justify-center',
          'transition-all duration-200',
          isVoiceActive
            ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
            : open
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-card border border-border text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary',
        )}
      >
        {isVoiceActive ? (
          voice.state === 'recording' ? (
            <MicOff className="w-5 h-5" />
          ) : (
            <Loader2 className="w-5 h-5 animate-spin" />
          )
        ) : open ? (
          <X className="w-5 h-5" />
        ) : (
          <Bot className="w-5 h-5" />
        )}
      </button>
    </>
  );
}
